import React from 'react';
import {Box, Paper, Stack, Typography} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {normalizeEeiPriority} from '../../../firebase/firestore/eei';
import {
    analyticAnswerBadge,
    collectionStatusBadge,
    isEeiFullyResolved,
} from './EeiCoverageStatusBadges';
import {COVERAGE_SOURCE} from '../coverage/coverageSourceTheme';

const PRIORITY_COLOR = {
    High:   '#c62828',
    Medium: '#f57c00',
    Low:    '#388e3c',
};

const STAGE_NAME_COLOR = {
    Data: COVERAGE_SOURCE.DATA.color,
    Analytics: COVERAGE_SOURCE.ANALYTICS.color,
};

const STAGE_PALETTE = {
    success: {dot: '#43a047', text: '#2e7d32', bg: '#f9fbe7', border: '#e0e0e0'},
    warning: {dot: '#fb8c00', text: '#bf360c', bg: '#fffde7', border: '#e0e0e0'},
    error:   {dot: '#ef5350', text: '#c62828', bg: '#fafafa',  border: '#e0e0e0'},
    default: {dot: '#90a4ae', text: '#546e7a', bg: '#fafafa',  border: '#e0e0e0'},
};

function StageDot({color}) {
    const palette = STAGE_PALETTE[color] || STAGE_PALETTE.default;
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-block',
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: palette.dot,
                flexShrink: 0,
            }}
        />
    );
}

function PipelineStage({stageName, badge}) {
    const palette = STAGE_PALETTE[badge.color] || STAGE_PALETTE.default;
    return (
        <Box
            sx={{
                minWidth: 128,
                border: `1px solid ${palette.border}`,
                borderRadius: '6px',
                bgcolor: palette.bg,
                px: 1.25,
                py: 0.65,
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    fontSize: '0.58rem',
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    color: STAGE_NAME_COLOR[stageName] || 'text.disabled',
                    mb: 0.3,
                }}
            >
                {stageName}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
                <StageDot color={badge.color}/>
                <Typography
                    variant="caption"
                    sx={{
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        lineHeight: 1.2,
                        color: palette.text,
                    }}
                >
                    {badge.label}
                </Typography>
            </Stack>
        </Box>
    );
}

function PipelineConnector() {
    return (
        <Stack alignItems="center" sx={{flexShrink: 0, px: 0.25}}>
            <ArrowForwardIcon sx={{fontSize: '1rem', color: 'text.disabled'}}/>
        </Stack>
    );
}

/**
 * Renders a list of EEI rows as pipeline cards.
 * Each card shows: priority + EEI number + text, then a
 * "Data → Analytics" two-stage pipeline.
 */
export default function EeiPipelineRows({rows = [], onNavigateToEei}) {
    if (rows.length === 0) return null;

    return (
        <Stack spacing={1}>
            {rows.map((row) => {
                const {
                    eei,
                    priority,
                    analyticStatus,
                    analyticPct,
                    collectionStatus,
                    collectionPct,
                } = row;

                const collBadge = collectionStatusBadge(collectionStatus, collectionPct);
                const ansBadge = analyticAnswerBadge(analyticStatus, analyticPct);
                const fullyResolved = isEeiFullyResolved(analyticStatus, collectionStatus);
                const rowKey = eei.id || eei.eeiNumber || eei.text;

                return (
                    <Paper
                        key={rowKey}
                        variant="outlined"
                        sx={{
                            borderRadius: '6px',
                            borderColor: fullyResolved ? 'divider' : 'grey.300',
                            opacity: fullyResolved ? 0.65 : 1,
                            transition: 'box-shadow 0.12s',
                            '&:hover': {
                                boxShadow: '0 1px 8px rgba(0,0,0,0.08)',
                                opacity: 1,
                            },
                        }}
                    >
                        <Stack
                            direction={{xs: 'column', sm: 'row'}}
                            alignItems={{sm: 'center'}}
                            spacing={0}
                            sx={{px: 1.75, py: 1.25, gap: {xs: 1, sm: 0}}}
                        >
                            {/* Left: identity + question */}
                            <Stack spacing={0.4} sx={{flex: 1, minWidth: 0, pr: {sm: 2}}}>
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                    <Typography
                                        component="span"
                                        variant="caption"
                                        fontFamily='"Roboto Mono", monospace'
                                        fontWeight={600}
                                        color={fullyResolved ? 'text.disabled' : 'primary'}
                                        sx={{
                                            cursor: onNavigateToEei ? 'pointer' : 'default',
                                            textDecoration: onNavigateToEei ? 'underline' : 'none',
                                            textDecorationColor: fullyResolved ? 'text.disabled' : undefined,
                                            whiteSpace: 'nowrap',
                                            letterSpacing: 0.2,
                                        }}
                                        onClick={onNavigateToEei ? () => onNavigateToEei(eei) : undefined}
                                    >
                                        {eei.eeiNumber || eei.id}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{fontSize: '0.72rem', whiteSpace: 'nowrap'}}
                                    >
                                        {'Priority: '}
                                        <Box
                                            component="span"
                                            fontWeight={700}
                                            sx={{color: PRIORITY_COLOR[normalizeEeiPriority(priority)] ?? 'text.primary'}}
                                        >
                                            {normalizeEeiPriority(priority)}
                                        </Box>
                                    </Typography>
                                </Stack>
                                <Typography
                                    variant="body2"
                                    color={fullyResolved ? 'text.secondary' : 'text.primary'}
                                    sx={{lineHeight: 1.55, wordBreak: 'break-word'}}
                                >
                                    {eei.text}
                                </Typography>
                            </Stack>

                            {/* Right: pipeline track */}
                            <Stack
                                direction="row"
                                alignItems="center"
                                sx={{flexShrink: 0}}
                            >
                                <PipelineStage stageName="Data" badge={collBadge}/>
                                <PipelineConnector/>
                                <PipelineStage stageName="Analytics" badge={ansBadge}/>
                            </Stack>
                        </Stack>
                    </Paper>
                );
            })}
        </Stack>
    );
}
